import { useState } from "react";
import { ChevronDown, ChevronUp, Pencil, Trash2, Tag, BookOpen } from "lucide-react";
import type { InterviewQuestion, InterviewCategory } from "../api/InterviewQaApi";
import RichTextDisplay from "shared/RichTextDisplay";
import ConfirmModal from "shared/ConfirmModal";

const DIFFICULTY_STYLE: Record<string, string> = {
  easy: "bg-emerald-500/10 text-emerald-400 border-emerald-500/20",
  medium: "bg-yellow-500/10 text-yellow-400 border-yellow-500/20",
  hard: "bg-red-500/10 text-red-400 border-red-500/20",
};

const STATUS_STYLE: Record<string, string> = {
  answered: "bg-indigo-500/10 text-indigo-400 border-indigo-500/20",
  unanswered: "bg-muted text-muted-foreground border-border",
};

interface QuestionCardProps {
  question: InterviewQuestion;
  categories: InterviewCategory[];
  canEdit: boolean;
  onEdit: (q: InterviewQuestion) => void;
  onDelete: (id: number) => void;
}

export default function QuestionCard({ question, categories, canEdit, onEdit, onDelete }: QuestionCardProps) {
  const [expanded, setExpanded] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [confirmingDelete, setConfirmingDelete] = useState(false);

  const category = categories.find((c) => c.id === question.category_id);

  async function confirmDelete() {
    setDeleting(true);
    try {
      await onDelete(question.id);
    } finally {
      setDeleting(false);
      setConfirmingDelete(false);
    }
  }

  return (
    <>
    {confirmingDelete && (
      <ConfirmModal
        title="Delete question"
        message="Delete this question?"
        confirmLabel="Delete"
        variant="danger"
        confirming={deleting}
        onConfirm={confirmDelete}
        onCancel={() => setConfirmingDelete(false)}
      />
    )}
    <div
      className="bg-surface/60 border border-border/60 rounded-xl p-4 hover:border-border transition-colors cursor-pointer select-none"
      onClick={() => setExpanded(!expanded)}
    >
      {/* Header row */}
      <div className="flex items-start gap-3">
        <div className="flex-1 min-w-0">
          <RichTextDisplay html={question.question} className="text-sm font-medium text-foreground leading-snug" />

          {/* Meta badges */}
          <div className="flex flex-wrap items-center gap-1.5 mt-2">
            <span className={`text-[10px] px-1.5 py-0.5 rounded border font-mono font-medium capitalize ${DIFFICULTY_STYLE[question.difficulty] ?? DIFFICULTY_STYLE.medium}`}>
              {question.difficulty}
            </span>
            <span className={`text-[10px] px-1.5 py-0.5 rounded border font-mono font-medium capitalize ${STATUS_STYLE[question.status] ?? STATUS_STYLE.unanswered}`}>
              {question.status}
            </span>
            {category && (
              <span className="text-[10px] px-1.5 py-0.5 rounded border bg-muted/40 text-muted-foreground border-border/40 font-inter">
                {category.name}
              </span>
            )}
            {(question.tags ?? []).map((t) => (
              <span key={t} className="flex items-center gap-0.5 text-[10px] px-1.5 py-0.5 rounded-full bg-surface-muted text-muted-foreground border border-border font-mono">
                <Tag className="w-2.5 h-2.5" />
                {t}
              </span>
            ))}
          </div>

          {/* Source */}
          {question.source && (
            <p className="text-[11px] text-muted-foreground mt-1.5 font-inter italic">
              {question.source}
            </p>
          )}
        </div>

        {/* Actions */}
        <div className="flex items-center gap-1 shrink-0">
          {canEdit && (
            <>
              <button
                onClick={(e) => { e.stopPropagation(); onEdit(question); }}
                className="p-1.5 text-muted-foreground hover:text-primary hover:bg-surface-muted rounded-lg transition-colors"
                title="Edit"
              >
                <Pencil className="w-3.5 h-3.5" />
              </button>
              <button
                onClick={(e) => { e.stopPropagation(); setConfirmingDelete(true); }}
                disabled={deleting}
                className="p-1.5 text-muted-foreground hover:text-destructive hover:bg-surface-muted rounded-lg transition-colors disabled:opacity-50"
                title="Delete"
              >
                <Trash2 className="w-3.5 h-3.5" />
              </button>
            </>
          )}
          <button
            onClick={(e) => { e.stopPropagation(); setExpanded(!expanded); }}
            className="p-1.5 text-muted-foreground hover:text-foreground hover:bg-surface-muted rounded-lg transition-colors"
            title={expanded ? "Collapse answer" : "Show answer"}
          >
            {expanded ? <ChevronUp className="w-3.5 h-3.5" /> : <ChevronDown className="w-3.5 h-3.5" />}
          </button>
        </div>
      </div>

      {/* Answer section */}
      {expanded && (
        <div className="mt-3 pt-3 border-t border-border/50">
          {question.answer ? (
            <RichTextDisplay html={question.answer} className="text-sm text-foreground" />
          ) : (
            <div className="flex items-center gap-2 text-muted-foreground text-sm font-inter italic">
              <BookOpen className="w-4 h-4" />
              No answer written yet.
            </div>
          )}
          {question.notes && (
            <div className="mt-3 p-3 rounded-lg bg-surface-muted/60 border border-border/50">
              <p className="text-[11px] text-muted-foreground font-inter font-medium mb-0.5">Notes</p>
              <p className="text-xs text-muted-foreground font-inter">{question.notes}</p>
            </div>
          )}
        </div>
      )}
    </div>
    </>
  );
}
